import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  EducatorTypeEnum,
  ProfessionalTypeEnum,
  ProsListMapGetProsQuery,
  ProsListMapGetProsQueryVariables,
} from '@gql/graphql';
import { CountryCode } from '@gql/graphql';
import type { MapBounds } from '@presentation/pages/ProList/components/Map/types';
import { useMemo } from 'react';

export const PROS_LIST_MAP_GET_PROS = graphql(`
  query ProsListMapGetPros(
    $take: Int
    $skip: Int
    $where: ProfessionalWhereInput
    $serviceId: Int
  ) {
    searchProfessionals(where: $where, take: $take, skip: $skip, serviceId: $serviceId) {
      professionals {
        id
        onlineBookingEnabled
        unclaimedProfile

        address {
          id
          lat
          lng
        }
      }
    }
  }
`);

export interface UseGetProsParams {
  type: ProfessionalTypeEnum;
  serviceId?: number;
  zoom?: number;
  bounds?: MapBounds;
  spokenLanguages?: string[];
  hasBooking?: boolean;
  educatorTypes?: EducatorTypeEnum[];
}

export const PROS_MAP_ZOOM_LEVEL_FOR_UNCLAIMED = 14;

export const useGetProsMap = (params: UseGetProsParams) => {
  const { type, serviceId, bounds, zoom, spokenLanguages, hasBooking, educatorTypes } = params;
  const { countryCode } = useRouter();
  const showOnlyClaimedPros = useMemo(() => {
    return (
      ![CountryCode.FR, CountryCode.NL, CountryCode.DE, CountryCode.CH, CountryCode.EU].includes(
        countryCode,
      ) &&
      zoom &&
      zoom < PROS_MAP_ZOOM_LEVEL_FOR_UNCLAIMED
    );
  }, [countryCode, zoom]);

  const take = useMemo(() => {
    if (Number.isInteger(zoom) && Number(zoom) < 8) {
      return 200;
    }
    return 100;
  }, [zoom]);

  const variables = useMemo((): ProsListMapGetProsQueryVariables => {
    return {
      ...(serviceId && { serviceId }),
      take,
      skip: 0,
      where: {
        AND: [
          { type: { equals: type } },
          { published: { equals: true } },
          { ...(hasBooking != null && { onlineBookingEnabled: { equals: hasBooking } }) },
          { ...(spokenLanguages?.length && { spokenLanguages: { hasSome: spokenLanguages } }) },
          {
            ...(showOnlyClaimedPros && { unclaimedProfile: { equals: false } }),
          },
          {
            ...(bounds && {
              address: {
                is: {
                  AND: [
                    { lat: { lte: bounds.northEast.lat } },
                    { lat: { gte: bounds.southWest.lat } },
                    { lng: { lte: bounds.northEast.lng } },
                    { lng: { gte: bounds.southWest.lng } },
                  ],
                },
              },
            }),
          },
          {
            ...(educatorTypes?.length && { educatorType: { in: educatorTypes } }),
          },
          ...(countryCode && countryCode !== CountryCode.EU
            ? [{ address: { is: { country: { equals: countryCode } } } }]
            : []),
        ],
      },
    } as ProsListMapGetProsQueryVariables;
  }, [
    take,
    bounds,
    countryCode,
    educatorTypes,
    hasBooking,
    serviceId,
    showOnlyClaimedPros,
    spokenLanguages,
    type,
  ]);

  const { data, loading } = useQuery<ProsListMapGetProsQuery, ProsListMapGetProsQueryVariables>(
    PROS_LIST_MAP_GET_PROS,
    { variables, skip: !bounds },
  );

  return {
    prosMap: data?.searchProfessionals.professionals ?? [],
    prosMapLoading: loading,
  };
};
